<template>
  <div
    class="item-card file-card"
    :class="{ disabled: isFileSelectionDisabled }"
    @click="selectFile"
  >
    <div class="item-card__image">
      <img v-lazy="fileBanner" alt="" v-if="fileBanner" />
      <icon-base
        v-else
        class="file-card-icon"
        :icon="fileIcon"
        :font-icon="portal"
      />

      <div class="file-card__status" v-if="isFileSelectionDisabled">
        {{ fileState || 'Processing...' }}
      </div>
    </div>

    <content-line
      :icon="fileIcon"
      :font-icon="portal"
      :text="file.filename"
      tooltip
    >
      <template #actions v-if="!file.default">
        <template v-if="gallery">
          <v-btn
            v-if="isScorm"
            fab
            icon
            small
            @click.stop="$emit('uploadFile')"
          >
            <v-icon color="appMain">cloud_upload</v-icon>
          </v-btn>

          <v-btn
            v-if="isFileSelectionDisabled"
            fab
            icon
            small
            @click.stop="refreshFile()"
          >
            <v-icon color="appMain">refresh</v-icon>
          </v-btn>

          <spinner small v-if="isFileDeleting" />
          <v-btn v-else fab icon small @click.stop="deleteFile()">
            <v-icon color="appMain">delete</v-icon>
          </v-btn>
        </template>

        <template v-else>
          <file-controls
            :file="file"
            :viewOnly="viewOnly"
            :deleteOnly="deleteOnly"
          />
        </template>
      </template>
    </content-line>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';

import FileControls from '@/views/files/files/FileControls.vue';
import EventBus from '@/factories/eventBus';

export default {
  name: 'FileCard',
  components: {
    FileControls,
  },
  props: {
    file: {
      type: Object,
      default: () => ({}),
    },
    assetOnly: Boolean,
    viewOnly: Boolean,
    gallery: Boolean,
    portal: Boolean,
    deleteOnly: Boolean,
  },
  data() {
    return {
      isFileDeleting: false,
    };
  },
  computed: {
    fileIcon() {
      return this.getFileTypeIcon(this.file.mimetype);
    },
    fileBanner() {
      return this.getFileBanner(this.file);
    },
    isScorm() {
      return this.file.type === 'scorm';
    },
    fileState() {
      return this.file?.asset?.state;
    },
    isFileSelectionDisabled() {
      if (!this.assetOnly) return false;
      return this.fileState !== 'ready';
    },
  },
  methods: {
    ...mapActions('files', ['deleteOne', 'loadOne']),
    ...mapMutations('fileFolders', ['removeFileFromFolder']),

    getFileBanner(file) {
      if (this.isFileSelectionDisabled || !file) return null;

      const { mimetype = '', asset = {}, url = '' } = file;

      const handleFrom = (u) => (u.split('/').pop() || '').split('?')[0];
      const isFilestack = url.includes('filestackcontent.com');
      const resizeParams = 'resize=width:640';

      // image
      if (mimetype.includes('image')) {
        return !this.disableResize && url
          ? this.minifyFilestackImage(url, mimetype)
          : url || null;
      }

      // pdf
      if (mimetype === 'application/pdf' && isFilestack) {
        const handle = handleFrom(url);
        return `https://cdn.filestackcontent.com/output=format:jpg,density:150,page:1/${resizeParams}/${handle}`;
      }

      if (mimetype.includes('video')) {
        // Mux
        const muxId = asset.playback_ids?.[0]?.id;
        if (muxId) {
          return `https://image.mux.com/${muxId}/thumbnail.png`;
        }

        // SproutVideo
        if (asset.thumb_big) {
          return asset.thumb_big;
        }

        // Filestack (fallback)
        if (isFilestack) {
          return `https://cdn.filestackcontent.com/video_convert=preset:thumbnail/${handleFrom(
            url
          )}`;
        }
      }

      // Default
      return null;
    },

    getFileTypeIcon(mime) {
      const icons = {
        video: 'play_circle',
        audio: 'music_note',
        image: 'image',
        file: 'description',
      };

      let type = 'file';

      if (typeof mime === 'string' && mime.includes('/')) {
        const [mainType] = mime.split('/');
        if (icons[mainType]) type = mainType;
      }

      return this.portal ? icons[type] : `custom/${type}`;
    },

    async deleteFile() {
      this.openConfirmationModal({
        title: 'Delete File',
        text: 'Are you sure you want to delete this file?',
        onConfirm: async () => {
          const { id, folder_id } = this.file;
          this.isFileDeleting = true;

          try {
            await this.deleteOne({ id });
            if (folder_id) {
              this.removeFileFromFolder({ folder_id, id });
            }
          } finally {
            this.isFileDeleting = false;
          }
        },
      });
    },

    refreshFile() {
      if (this.isFileSelectionDisabled && this.assetOnly) {
        this.loadOne({ path: this.file.id });
      }
    },

    selectFile() {
      if (!this.gallery) {
        EventBus.$emit('openFileViewer', this.file);
        return;
      }

      if (this.isFileSelectionDisabled || !this.editable) return;

      this.$emit('selectFile');
    },
  },
  mounted() {
    this.refreshFile();
  },
};
</script>

<style>
.file-card__status {
  position: absolute;
  top: 0;
  right: 0;
  padding: 4px 8px;
  color: var(--v-light-base);
  background: var(--v-warning-base);
}

.file-card {
  .item-card__image {
    background: var(--v-darkGray-base);
    display: flex;
    align-items: center;
    justify-content: center;

    .file-card-icon {
      height: 70%;
      top: 15%;

      &.v-icon {
        display: block;
        margin: auto;
        height: auto;
        width: auto;
        position: relative;
        font-size: 60px;
        color: var(--v-primary-base) !important;
      }
    }
  }
}
</style>
